import {
    Badge,
    BlockStack,
    Button,
    Card,
    Grid,
    Icon,
    InlineStack,
    List,
    Page,
    Text,
    Toast,
} from "@shopify/polaris";
import {
    CheckCircleIcon,
    CircleIcon,
    LockIcon,
    ViewIcon,
} from "@shopify/polaris-icons";
import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppFormik } from "../contexts/FormikContext";
import { useDispatch, useSelector } from "react-redux";
import { createSettingAsync, getAllSetting, getSettingAsync } from "../redux/slice/commonSlice";
import { config_variable } from "../helper/commonApi";
import onboardGif from "../assets/image/onboard.gif";

const OnboardingPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { values, setFieldValue } = useAppFormik();
    const { isCreateSettingLoading, isEmbedEnabled } = useSelector(getAllSetting);
    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [currentStep, setCurrentStep] = useState(values.onboarding?.step || 1);

    const isStoreLockActive = isEmbedEnabled || false;

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const toastMarkup = toastActive ? (
        <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
    ) : null;

    const handleNext = async () => {
        if (currentStep === 1) {
            // Update step to 2
            const updatedValues = {
                ...values,
                onboarding: {
                    ...values.onboarding,
                    step: 2
                }
            };
            setFieldValue("onboarding.step", 2);
            await dispatch(createSettingAsync({
                payload: { settings: updatedValues },
                callback: async () => {
                    setCurrentStep(2);
                    setIsCheckingStatus(true);
                    await dispatch(getSettingAsync({}));
                    setIsCheckingStatus(false);
                }
            }));
        }
    };

    const handleEnableStoreLock = () => {
        window.open(
            `https://${config_variable.shop_name}/admin/themes/current/editor?context=apps&template=head&activateAppId=${config_variable?.config?.apiKey}/main`,
            "_blank"
        );
        setIsCheckingStatus(true);
    };

    const handleContinue = async () => {
        if (isStoreLockActive) {
            // Mark onboarding as completed
            const updatedValues = {
                ...values,
                onboarding: {
                    ...values.onboarding,
                    is_completed: true,
                    step: 1
                }
            };

            // Save settings
            await dispatch(createSettingAsync({
                payload: { settings: updatedValues },
                callback: () => {
                    dispatch(getSettingAsync({}));
                    // Redirect to settings page
                    navigate("/setting");
                }
            }));
        }
    };

    // Step 1: Introduction
    if (currentStep === 1) {
        return (
            <Page
                title="Welcome to StoreLock"
                primaryAction={{
                    content: "Next",
                    onAction: handleNext,
                }}
            >
                <BlockStack gap="500">
                    <Card>
                        <Grid>
                            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                                <BlockStack gap="400">
                                    <Text variant="headingLg" as="h1">
                                        StoreLock
                                    </Text>
                                    <Text variant="bodyMd" tone="subdued">
                                        Protect your Shopify store from unauthorized access, content copying, and malicious activities.
                                        StoreLock provides comprehensive security features to safeguard your storefront and customer data.
                                    </Text>
                                    <BlockStack gap="300">
                                        <Text variant="headingMd" as="h2">
                                            Key Features:
                                        </Text>
                                        <List type="bullet">
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Content Protection:</Text> Prevent text and image copying
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Developer Tools Blocking:</Text> Disable browser dev tools access
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Right-Click Protection:</Text> Block context menu access
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Keyboard Shortcut Protection:</Text> Block common shortcuts (Ctrl+P, Ctrl+S, etc.)
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Geo Blocking:</Text> Restrict access by country
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">IP Address Blocking:</Text> Block specific IP addresses
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Redirect Prevention:</Text> Control external link redirects
                                            </List.Item>
                                            <List.Item>
                                                <Text as="span" fontWeight="semibold">Activity Logging:</Text> Monitor and track security events
                                            </List.Item>
                                        </List>
                                    </BlockStack>
                                </BlockStack>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                                <BlockStack gap="300">
                                    <Text variant="headingMd" as="h2">
                                        See It In Action
                                    </Text>
                                    <div style={{ width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
                                        <img
                                            src={onboardGif}
                                            alt="StoreLock Introduction"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                borderRadius: '8px'
                                            }}
                                        />
                                    </div>
                                </BlockStack>
                            </Grid.Cell>
                        </Grid>
                    </Card>
                </BlockStack>
                {toastMarkup}
            </Page>
        );
    }

    // Step 2: Enable StoreLock
    return (
        <Page
            title="Enable StoreLock"
            primaryAction={{
                content: "Continue",
                onAction: handleContinue,
                disabled: !isStoreLockActive || isCheckingStatus,
                loading: isCreateSettingLoading,
            }}
        >
            <Card>
                <BlockStack gap="400">
                    <InlineStack gap="200" blockAlign="start" align="start">
                        <Text variant="headingMd" as="h2">
                            Enable StoreLock
                        </Text>
                        <Badge
                            tone={isStoreLockActive ? "success" : "critical"}
                            size="large"
                        >
                            {isStoreLockActive ? "Active" : "Inactive"}
                        </Badge>
                    </InlineStack>
                    <Text tone="subdued" variant="bodySm">
                        Activate StoreLock to start protecting your store. Click the button below to enable the app block in your theme editor.
                    </Text>
                    <InlineStack gap="200" blockAlign="start" align="start">
                        <Button
                            variant="primary"
                            onClick={handleEnableStoreLock}
                            loading={isCheckingStatus}
                            disabled={isCheckingStatus || isStoreLockActive}
                            
                        >
                            {isStoreLockActive ? 'Enabled StoreLock' : 'Enable StoreLock'}
                        </Button>
                    </InlineStack>
                    <Text tone="subdued" variant="bodySm">
                        You'll be redirected to the theme editor. After enabling the app block, return here to continue.
                    </Text>
                </BlockStack>
            </Card>
            {toastMarkup}
        </Page>
    );
};
export default OnboardingPage;
